import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as urlTemplate from 'url-template';
import { map, tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

import { User, UserInfo } from '@app/modules/core/interfaces/user';
import {
  userApi, userAddressesApi, seamlessLoginApi, authSeamlessLoginApi
} from '@app/modules/core/api/user.api';
import { UserAddresses, Address, NewAddress, AddressResponse, City } from '@app/modules/core/interfaces/address';
import { AuthService } from '@app/services/auth.service';
import { AlertResponse } from '@shared/widgets/entities/alerts';
import { widgetsApi } from '@shared/widgets/widgets.api';
import { environment as ENV } from '@environment';
import { AuthSeamlessLoginPayload } from '@app/entities/auth-seamless-login.payload';
import { AuthSeamlessLoginResponse } from '@app/entities/auth-seamless-login.response';
import { IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { isNullOrUndefined } from '@app/util/util';
import { TravelCmsHomePayload } from '@home/entities/travel-cms';
import { USER_ID, homeApi } from '@home/home.api';
import { HomeDataResponse } from '@shared/home-sections/entities/home-data.response';
import { GetUserBenefitsPayload, UserBenefitResponse } from '@shared/benefits/entities/benefits';
import { encodeURIParams } from '@app/mappers/encoding.mapper';
import { BenefitsCookie } from '@app/constants/cookies';
import { StaticList } from '@app/modules/core/interfaces/static-lists';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  
  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly cookieService: CookieService,
    @Inject(IS_MOBILE) public isMobile: boolean
  ) { }

  public getData(): Observable<User> {
    return this.http.get<User>(userApi.data.url);
  }

  public getUserInfo(): Observable<UserInfo> {
    return this.http.get<UserInfo>(userApi.info.url);
  }

  public getUserBenefits(payload: GetUserBenefitsPayload): Observable<UserBenefitResponse[]> {
    return this.http.post<UserBenefitResponse[]>(userApi.get_benefits.url, payload).pipe(
      tap(benefits => {
        this.setBenefitsCookie(benefits);
      })
    );
  }

  public fetchStaticLists(): Observable<StaticList[]> {
    return this.http.get<(StaticList[])>(userApi.statics_lists.url);
  }

  public fetchPageData(payload: TravelCmsHomePayload): Observable<HomeDataResponse> {
    const params = {};
    if (ENV.x_application_id) {
      params['X_Application_ID'] = ENV.x_application_id;
    }
    const headers = new HttpHeaders(params);
    let url = urlTemplate.parse(homeApi.home_data).expand({
      pageCode: ENV.localeCMS,
      userId: payload.userId,
      isPrime: payload.isPrime,
      loyalty: payload.loyalty,
      isMobile: this.isMobile,
      treatments: payload.treatments,
      levelPrime: payload.levelPrime
    });
    if (isNullOrUndefined(payload.userId)) {
      const sliceIndex = url.indexOf(`&${USER_ID}`);
      url = url.substr(0, sliceIndex);
    }
    return this.http.get<HomeDataResponse>(url, { headers });
  }

  public getUserAddresses(): Observable<UserAddresses> {
    return this.http.get<UserAddresses>(userAddressesApi.data.url);
  }

  public getCountryCities(): Observable<City[]> {
    return this.http.get<City[]>(userAddressesApi.cities.url);
  }

  public activeUserAddresses(address: Address): Observable<Address> {
    return this.http.put<Address>(userAddressesApi.active.url, address);
  }

  public createUserAddress(payload: NewAddress): Observable<AddressResponse> {
    return this.http.post<AddressResponse>(userAddressesApi.create.url, payload);
  }

  public fetchUserAlerts(user_id: string | number): Observable<AlertResponse[]> {
    const headers = new HttpHeaders({
      'auth_user': `${user_id}`,
      'x_application_id': ENV.x_application_id
    });
    const url = urlTemplate.parse(widgetsApi.alerts.get_user_alerts).expand({
      id: user_id,
    });
    return this.http.get<AlertResponse[]>(url, { headers });
  }

  public seamlessLogin(payload: string): Observable<string> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${payload}`
    });

    return this.http.get<string>(seamlessLoginApi.auth_user.url, { headers: headers }).pipe(
      tap(session => this.setSeamlessLoginCookie(session)),
      map(session => session)
    );
  }


  public authseamlessLoginToken(payload: AuthSeamlessLoginPayload): Observable<AuthSeamlessLoginResponse> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${payload.SessionToken}`,
      'ExternalToken': payload.ExternalToken
    });
    return this.http.get<AuthSeamlessLoginResponse>(authSeamlessLoginApi.url, { headers });
  }

  public requestSession(): Observable<string> {
    return this.http
      .get<string>(seamlessLoginApi.guest_user.url).pipe(
        tap(session => {
          this.setSeamlessLoginCookie(session);
        }),
        map(session => session)
      );
  }

  private setSeamlessLoginCookie(session: string): void {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    this.cookieService.set('SeamlessLogin', session, date, '/');
  }

  private setBenefitsCookie(benefits: UserBenefitResponse[]): void {
    const date = new Date();
    date.setHours(date.getHours() + 12);
    this.cookieService.set(BenefitsCookie, encodeURIParams(JSON.stringify(benefits)), date, '/');
  }
}
